// Backend OTP Service - Dual Channel (WhatsApp + Email)
// OTP is generated on backend for security
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const backendOTPService = {
  // Send OTP to both Email and WhatsApp (backend generates OTP securely)
  sendOTP: async (email, phone, userName = '') => {
    try {
      const response = await fetch(`${API_URL}/api/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, phone, userName }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      return {
        success: true,
        message: data.message,
        email: data.email,
        phone: data.phone,
        whatsapp: data.whatsapp,
        emailDelivery: data.emailDelivery,
        expiresAt: data.expiresAt,
        // OTP is never exposed to frontend for security
      };
    } catch (error) {
      console.error('[Backend OTP Service] Error:', error);
      return {
        success: false,
        error: error.message || 'Failed to send OTP',
      };
    }
  },

  // Verify OTP on backend (secure validation)
  verifyOTP: async (email, phone, otp) => {
    try {
      const response = await fetch(`${API_URL}/api/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, phone, otp }),
      });

      const data = await response.json();

      return {
        success: data.success,
        valid: data.valid,
        reason: data.reason,
      };
    } catch (error) {
      console.error('[Backend OTP Service] Verify Error:', error);
      return {
        success: false,
        valid: false,
        reason: error.message || 'Verification failed',
      };
    }
  },

  // Resend OTP via both channels
  resendOTP: async (email, phone, userName = '') => {
    try {
      const response = await fetch(`${API_URL}/api/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, phone, userName }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend OTP');
      }

      return {
        success: true,
        message: data.message,
        whatsapp: data.whatsapp,
        emailDelivery: data.emailDelivery,
        expiresAt: data.expiresAt,
        // OTP is never exposed to frontend for security
      };
    } catch (error) {
      console.error('[Backend OTP Service] Resend Error:', error);
      return {
        success: false,
        error: error.message || 'Failed to resend OTP',
      };
    }
  },

  // Check if backend is available
  healthCheck: async () => {
    try {
      const response = await fetch(`${API_URL}/api/health`);
      const data = await response.json();
      return data.status === 'ok';
    } catch (error) {
      console.error('[Backend OTP Service] Backend not available');
      return false;
    }
  },
};

// Keep backwards compatibility
export const emailOTPService = backendOTPService;

// ========================================
// DOCUMENT SUBMISSION NOTIFICATION SERVICE
// ========================================

export const documentNotificationService = {
  // Send Bank Proof Submission Notification with attachment
  // Accepts either a File object (documentFile) OR pre-converted base64 (documentBase64, documentName, documentType)
  sendBankProofNotification: async ({
    email,
    userName,
    bankName,
    iban,
    accountNumber,
    accountTitle,
    submissionId,
    status = 'pending', // 'pending', 'approved', 'rejected'
    documentFile, // File object (optional)
    documentBase64: preConvertedBase64, // Pre-converted base64 (optional)
    documentName: preConvertedName, // Pre-converted name (optional)
    documentType: preConvertedType, // Pre-converted type (optional)
  }) => {
    try {
      // Use pre-converted base64 if provided, otherwise convert from file
      let documentBase64 = preConvertedBase64 || null;
      let documentName = preConvertedName || null;
      let documentType = preConvertedType || null;

      if (!documentBase64 && documentFile) {
        documentBase64 = await fileToBase64(documentFile);
        documentName = documentFile.name || `bank-proof-${submissionId}.jpg`;
        documentType = documentFile.type || 'image/jpeg';
      }

      const response = await fetch(`${API_URL}/api/send-bank-proof-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          userName,
          bankName,
          iban,
          accountNumber,
          accountTitle,
          submissionId,
          status,
          documentBase64,
          documentName,
          documentType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send bank proof notification');
      }

      console.log('[Email] Bank proof notification sent:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('[Email] Bank proof notification error:', error);
      return { success: false, error: error.message };
    }
  },

  // Send Payment Receipt Submission Notification with attachment
  // Accepts either a File object (documentFile) OR pre-converted base64 (documentBase64, documentName, documentType)
  sendReceiptNotification: async ({
    email,
    userName,
    transactionId,
    referenceNumber,
    amount,
    bankName,
    accountTitle,
    status = 'submitted', // 'submitted', 'verified', 'mismatch', 'manual_review'
    ocrResult,
    mismatchDetails,
    documentFile, // File object (optional)
    documentBase64: preConvertedBase64, // Pre-converted base64 (optional)
    documentName: preConvertedName, // Pre-converted name (optional)
    documentType: preConvertedType, // Pre-converted type (optional)
  }) => {
    try {
      // Use pre-converted base64 if provided, otherwise convert from file
      let documentBase64 = preConvertedBase64 || null;
      let documentName = preConvertedName || null;
      let documentType = preConvertedType || null;

      if (!documentBase64 && documentFile) {
        documentBase64 = await fileToBase64(documentFile);
        documentName = documentFile.name || `receipt-${referenceNumber}.jpg`;
        documentType = documentFile.type || 'image/jpeg';
      }

      // Log attachment status for debugging
      if (documentBase64) {
        const base64Size = Math.round(documentBase64.length * 0.75 / 1024);
        console.log(`[Email] Receipt notification - Attachment: ${documentName} (~${base64Size} KB)`);
      } else {
        console.warn(`[Email] Receipt notification - NO ATTACHMENT (base64 is null) for transaction ${transactionId}`);
      }

      const response = await fetch(`${API_URL}/api/send-receipt-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          userName,
          transactionId,
          referenceNumber,
          amount,
          bankName,
          accountTitle,
          status,
          ocrResult,
          mismatchDetails,
          documentBase64,
          documentName,
          documentType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send receipt notification');
      }

      console.log('[Email] Receipt notification sent:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('[Email] Receipt notification error:', error);
      return { success: false, error: error.message };
    }
  },
};

// Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// ========================================
// ADMIN DECISION NOTIFICATION SERVICE
// ========================================

export const adminDecisionNotificationService = {
  // Send Account Approval/Rejection Notification
  sendAccountDecisionNotification: async ({
    email,
    userName,
    bankName,
    iban,
    accountNumber,
    accountTitle,
    submissionId,
    decision, // 'approved' or 'rejected'
    rejectionReason,
    adminComment,
    documentBase64,
    documentName,
    documentType,
  }) => {
    try {
      const response = await fetch(`${API_URL}/api/send-account-decision-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          userName,
          bankName,
          iban,
          accountNumber,
          accountTitle,
          submissionId,
          decision,
          rejectionReason,
          adminComment,
          documentBase64,
          documentName,
          documentType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send account decision notification');
      }

      console.log('[Email] Account decision notification sent:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('[Email] Account decision notification error:', error);
      return { success: false, error: error.message };
    }
  },

  // Send Transaction Approval/Rejection Notification
  sendTransactionDecisionNotification: async ({
    email,
    userName,
    transactionId,
    referenceNumber,
    amount,
    bankName,
    accountTitle,
    decision, // 'approved' or 'rejected'
    rejectionReason,
    adminComment,
    documentBase64,
    documentName,
    documentType,
  }) => {
    try {
      const response = await fetch(`${API_URL}/api/send-transaction-decision-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          userName,
          transactionId,
          referenceNumber,
          amount,
          bankName,
          accountTitle,
          decision,
          rejectionReason,
          adminComment,
          documentBase64,
          documentName,
          documentType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send transaction decision notification');
      }

      console.log('[Email] Transaction decision notification sent:', data);
      return { success: true, ...data };
    } catch (error) {
      console.error('[Email] Transaction decision notification error:', error);
      return { success: false, error: error.message };
    }
  },
};

export default backendOTPService;
